<template>
  <nav class="horizontal-nav" aria-label="Điều hướng chính">
    <template v-for="route in visibleRoutes" :key="route.name">
      <RouterLink v-if="!route.children" class="horizontal-link" :to="{ name: route.name }" :aria-current="isActive(route) ? 'page' : undefined">
        <VaIcon :name="route.meta.icon" size="small" />{{ t(route.displayName) }}
      </RouterLink>
      <div v-else class="horizontal-group" :class="{ active: routeHasActiveChild(route), open: openGroup === route.name }">
        <button type="button" :aria-expanded="openGroup === route.name" @click="toggleGroup(route.name)"><VaIcon :name="route.meta.icon" size="small" />{{ t(route.displayName) }}<VaIcon :name="openGroup === route.name ? 'va-arrow-up' : 'va-arrow-down'" size="small" /></button>
        <div v-if="openGroup === route.name" class="horizontal-menu">
          <RouterLink v-for="child in route.children" :key="child.name" :to="{ name: child.name }" :class="{ active: isActive(child) }" @click="openGroup = null">
            <VaIcon :name="child.meta.icon" size="small" />{{ t(child.displayName) }}
          </RouterLink>
        </div>
      </div>
    </template>
  </nav>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '../../stores/auth'
import navigationRoutes, { type INavigationRoute } from '../sidebar/NavigationRoutes'

const route = useRoute()
const auth = useAuthStore()
const { t } = useI18n()
const openGroup = ref<string | null>(null)
function toggleGroup(name: string) {
  openGroup.value = openGroup.value === name ? null : name
}
const visibleRoutes = computed(() => navigationRoutes.routes.flatMap((item) => {
  const children = item.children?.filter((child) => !child.permission || auth.user?.effective_permissions?.includes(child.permission))
  if (item.permission && !auth.user?.effective_permissions?.includes(item.permission)) return []
  if (item.children && !children?.length) return []
  return [{ ...item, ...(item.children ? { children } : {}) }]
}))
const isActive = (item: INavigationRoute) => route.name === item.name
const routeHasActiveChild = (item: INavigationRoute) => item.children?.some(isActive) ?? isActive(item)
</script>

<style scoped>
.horizontal-nav { position: relative; z-index: 50; display: flex; align-items: center; flex-wrap: wrap; gap: .2rem; min-height: 52px; padding: .3rem 1.25rem; overflow: visible; border-bottom: 1px solid var(--va-background-border); background: var(--va-background-element); box-shadow: 0 2px 6px rgb(30 41 59 / 4%); }
.horizontal-link, .horizontal-group > button { display: inline-flex; align-items: center; gap: .5rem; padding: .7rem .85rem; border: 0; border-bottom: 2px solid transparent; border-radius: 7px 7px 0 0; background: transparent; color: var(--va-text-secondary); font-size: .875rem; font-weight: 600; text-decoration: none; white-space: nowrap; cursor: pointer; }
.horizontal-link:hover, .horizontal-group > button:hover { background: var(--va-background-secondary); color: var(--va-primary); }
.horizontal-link[aria-current='page'], .horizontal-group.active > button, .horizontal-group.open > button { border-bottom-color: var(--va-primary); background: var(--va-background-secondary); color: var(--va-primary); }
.horizontal-group { position: relative; }
.horizontal-group.open { z-index: 51; }
.horizontal-menu { position: absolute; z-index: 1000; top: calc(100% + .25rem); left: 0; display: grid; min-width: 220px; padding: .4rem; border: 1px solid var(--va-background-border); border-radius: 9px; background: var(--va-background-element); box-shadow: 0 8px 24px rgb(15 23 42 / 16%); }
.horizontal-menu a { display: flex; align-items: center; gap: .6rem; padding: .65rem .75rem; border-radius: 6px; color: var(--va-text-primary); font-size: .85rem; text-decoration: none; }
.horizontal-menu a:hover, .horizontal-menu a.active { background: var(--va-background-secondary); color: var(--va-primary); }
</style>
